import {
  type EventId,
  type FigureId,
  isOnOrBefore,
  isSameDate,
  type SettlementView,
  type SpotlightBeat,
  type WorldDateView,
} from "../world/types";

/*
 * Battle staging: the town's reaction to a record landing.
 *
 * Phase 4 decision 5 — **reaction, never reenactment**. A battle is one record
 * appended on one tick, by design and permanently (history §6): there is no
 * skirmish to play out, no units to move, and any attempt to show one would be
 * the client inventing the middle of a thing the simulation only ever recorded
 * the end of. What a player *can* be shown is what a town a road away actually
 * sees of it — the street turning out, the same sentence in three mouths before
 * anybody asks, a number on the board that moved this morning, and smoke on the
 * horizon in the direction it came from.
 *
 * # What this module is allowed to decide
 *
 * Timing, and nothing else. The rule the phase's cross-cutting section states —
 * *salience is sim-side, scheduling is client-side* — is what the two halves of
 * this file are split along:
 *
 * - **The simulation decides there is something worth meeting.** A beat exists
 *   or it does not ({@link SpotlightBeat}); the client never invents one, never
 *   re-ranks the list it was handed, and never stages a beat before
 *   {@link SpotlightBeat.earliest}. {@link stageableBeat} is the whole of the
 *   selection and it is three comparisons, all of them about *dates and
 *   places* rather than about importance — plus whatever the caller adds, which
 *   since wave 3A is the client asking whether it has an honest picture of a
 *   given beat at all (`witness/director.ts`).
 * - **The client decides when the player meets it.** Which figure speaks is
 *   whoever is nearest and has not spoken yet; how long the town stays turned
 *   out is a constant below; where the smoke stands is geometry. All of it
 *   depends on where the player's body is, which is the one input the sim can
 *   never hold (Phase 3 decision 10 — the player is not a sim entity).
 *
 * # What it never does
 *
 * Compose a sentence. A staged line is `WitnessSource.speakOf`'s, which is
 * `speech::line` with the record named instead of chosen — a crier is
 * proximity-priority *speech*, not new prose (decision 5). This module returns a
 * request naming a figure and a record; `witness/witness.ts` asks the simulation
 * and shows whatever comes back, including nothing.
 */

/**
 * How long the town stays turned out, in seconds of stepped time. PROVISIONAL.
 *
 * **Re-derived at twenty-minute days and it did not move** (Phase 5 decision
 * 9), which is worth writing down because most of the pacing around it did.
 * The two things this number is measured against are both facts about the
 * *player's body* rather than about the calendar: long enough that somebody who
 * was across the settlement when it started can still walk into it — the crowd
 * ring is a dozen tiles and a walk is a few seconds — and short enough that a
 * town does not spend a session standing in a ring. Neither of those changes
 * when a day gets thirty times longer, because a player does not walk thirty
 * times slower.
 *
 * What the retune fixes is the *sentence* underneath it. Ninety seconds used to
 * be "a little over two in-game days", which meant the street was turned out
 * over the news for longer than the news took to become last week's — nobody
 * noticed, because nobody watched two whole days. It is now about a
 * thirteenth of a day, which is a morning, which is what a town turning out
 * over something was always supposed to be.
 *
 * It stays a constant rather than joining the director's knobs
 * (`witness/director.ts`'s `DirectorConfig`): those are about *when and how
 * often* a reaction happens, which is scheduling, and this is about how long
 * one takes, which is the reaction itself.
 */
const STAGING_SECONDS = 90;

/**
 * How near a figure has to be before they say it unprompted, in tiles.
 *
 * Three times the interact range (`witness/interact.ts`), because this is the
 * opposite verb: F is the player addressing somebody they have walked up to, and
 * this is somebody within earshot saying something to the street. Wider than a
 * conversation, narrower than the settlement — a town where every voice carries
 * from anywhere is a town with one voice.
 */
const EARSHOT_TILES = 9;

/**
 * How long after the beat lands the first voice speaks, and how long between
 * voices, in seconds. Both PROVISIONAL.
 *
 * The gap is a little longer than a spoken line holds at full strength
 * (`hud/panels.ts`'s seven seconds is the fade, not the read), so two criers
 * never overwrite each other mid-sentence, and the delay exists because a
 * reaction that is already finished when the player turns round did not happen
 * as far as they are concerned.
 */
const FIRST_VOICE_SECONDS = 1.5;
const BETWEEN_VOICES_SECONDS = 5.5;

/**
 * How far away the smoke is drawn, in tiles, when the burning town is further
 * off than that.
 *
 * **Presentation of a real bearing**, and the same latitude the straight road
 * takes (decision 4): the direction is the simulation's — it is where the
 * subject settlement stands — and the distance is the client's, because past the
 * streaming window there is nothing drawn at all and a plume that only existed
 * when the sacked town happened to be inside the ring would be missing in
 * exactly the sessions that need it. Inside this distance the smoke goes over
 * the town itself, which is the honest place for it.
 *
 * Forty tiles is half the view distance (`world/wasm.ts`), so the column stands
 * in the middle distance where the fog has begun to take it — which is what
 * distant smoke looks like — rather than at the horizon where the fog has
 * finished.
 */
const SMOKE_DISTANCE_TILES = 40;

/**
 * What this module needs to know about a body: who it is, and where it is drawn.
 *
 * **Narrower than any of the three lists that satisfy it**, and that is the
 * point of declaring it here. The voices used to be picked out of the client's
 * own ambient crowd, which since item 6 draws no named figures at all (ruled
 * 2026-08-28, *one owner per body*) — so what a crier is chosen from now is the
 * **simulation's** presence list, at the position it is being drawn at
 * (`witness/witness.ts`). A shape with two coordinates and a handle is all this
 * file has ever used, and taking that rather than a concrete list is what keeps
 * the choosing checkable without a world behind it.
 */
export interface StagedBody {
  readonly figure: FigureId;

  /** Where it is drawn, in world tiles. */
  readonly x: number;
  readonly y: number;
}

/** A figure the staging wants to hear from, and the record it wants them to speak to. */
export interface CrierRequest {
  readonly figure: FigureId;
  readonly event: EventId;
}

/** Where the smoke stands, in world tiles, and which way its plume leans. */
export interface Plume {
  readonly x: number;
  readonly y: number;
  readonly facing: number;
}

/**
 * Returns the beat a town would visibly react to today, or `null`.
 *
 * Three conditions, and every one of them is a fact rather than a judgement:
 *
 * - the beat is about **somewhere else** — a town does not turn out for its own
 *   market moving, and the subject is what gives the smoke a direction;
 * - it lands **today**, because a reaction to something the street has been
 *   talking about for a week is not a reaction;
 * - it has **not been staged before**, so a day boundary that re-reads the same
 *   list does not start the same morning over.
 *
 * The list is walked in the order the source gave it and the first match wins.
 * That is deliberate and it is the plan's rule: which material is worth meeting
 * is the projection's judgement, and a client that sorted the list would be
 * making a gameplay decision in JavaScript (devlog #2).
 *
 * `fits` is the caller's own veto and it is passed in rather than written here
 * because it is a different *kind* of question: these three conditions are facts
 * about a beat, and that one is the client asking whether it has a truthful way
 * to draw this particular beat (`witness/director.ts`'s `fitsTheStreet`).
 * Filtering, never ordering — a beat it refuses is skipped where it stands, and
 * the beat after it is considered in the position the source put it in.
 */
export function stageableBeat(
  beats: readonly SpotlightBeat[],
  here: number,
  today: WorldDateView,
  staged: ReadonlySet<number>,
  fits: (beat: SpotlightBeat) => boolean = () => true,
): SpotlightBeat | null {
  for (const beat of beats) {
    if (staged.has(beat.id) || beat.subject === null || beat.subject === here) {
      continue;
    }

    if (!fits(beat)) {
      continue;
    }

    // Landing today, which is also — and not incidentally — a beat that has
    // landed at all: `isSameDate` can never be true for a date in the future,
    // and `isOnOrBefore` is asserted beside it so the intent survives somebody
    // widening the window later.
    if (isSameDate(beat.earliest, today) && isOnOrBefore(beat.earliest, today)) {
      return beat;
    }
  }

  return null;
}

/**
 * One staged reaction, from the morning it lands to the moment the street goes
 * back to what it was doing.
 *
 * Holds no DOM, no scene and no source: it is handed the bodies and the player's
 * position on each step and answers with who should speak. That is what makes it
 * checkable in `dev/selfcheck.ts` without a browser, and it is the same split
 * `journal/journal.ts` and `witness/interact.ts` are written under.
 */
export class BattleStaging {
  /** The beat being staged, or `null` when the town is going about its business. */
  private staged: SpotlightBeat | null = null;

  /** Where the smoke stands while it is, in world tiles. */
  private smoke: Plume | null = null;

  /** Seconds of stepped time since it landed. */
  private elapsed = 0;

  /** Seconds since the last voice, and who has already spoken about it. */
  private sinceVoice = 0;
  private readonly spoken = new Set<FigureId>();

  /** Whether the town is currently reacting to something. */
  get active(): boolean {
    return this.staged !== null;
  }

  /** The beat being staged, for the client to file and trace against. */
  get beat(): SpotlightBeat | null {
    return this.staged;
  }

  /** Where the smoke goes, or `null` when nothing is burning in view. */
  get plume(): Plume | null {
    return this.smoke;
  }

  /**
   * Starts a reaction to a beat.
   *
   * `struck` is the settlement the smoke stands over and `here` the one the
   * player is standing in; both are needed for the plume and neither for
   * anything else. A reaction with no `struck` still turns the street out and
   * still speaks — it simply has nothing to point at.
   *
   * **The caller decides what may be pointed at, and it is stricter than "the
   * beat's subject".** A plume is a claim that a town is burning, and the live
   * projection's `subject` means a different thing on every surface — who told
   * us, the other end of a road, the army's home. So `witness/witness.ts` passes
   * a town only where a second record says that town was struck: a refugee
   * column on the road out of it, fleeing the same record this beat carries.
   * Everywhere else this is `null` and no smoke is drawn, which is the honest
   * picture of news arriving with nothing visible behind it.
   */
  begin(beat: SpotlightBeat, struck: SettlementView | null, here: SettlementView | null): void {
    this.staged = beat;
    this.elapsed = 0;
    // The first voice waits {@link FIRST_VOICE_SECONDS}, so the clock starts
    // that far short of the gap rather than at it.
    this.sinceVoice = BETWEEN_VOICES_SECONDS - FIRST_VOICE_SECONDS;
    this.spoken.clear();
    this.smoke = plumeFor(struck, here);
  }

  /**
   * Advances the reaction and returns the figure who should speak now, if any.
   *
   * Nearest-first and once each: the town is a couple of dozen bodies, the
   * player is standing among them, and "whoever is closest and has not said it
   * yet" is the rule a player would describe as *word going round*. A figure who
   * has spoken is remembered for the whole reaction, so nobody repeats
   * themselves and the voices move outwards as the player walks.
   *
   * **The bodies are the simulation's and they are not asked to come** (item 6,
   * rider 3). The reaction used to ring the client's own crowd around the market
   * board and pick a voice out of the ring; the ring is gone with the loop's
   * figure-drawing, so what is left is the honest half — a person who happens to
   * be near enough says what landed this morning, and the town does not turn out
   * because the client cannot ask it to. *Making* it turn out is a sim behaviour
   * and is filed with the conversation area's engagement work.
   */
  step(
    stepSeconds: number,
    bodies: readonly StagedBody[],
    from: { readonly x: number; readonly y: number },
  ): CrierRequest | null {
    const beat = this.staged;

    if (beat === null) {
      return null;
    }

    this.elapsed += stepSeconds;
    this.sinceVoice += stepSeconds;

    if (this.elapsed >= STAGING_SECONDS) {
      this.end();

      return null;
    }

    if (this.sinceVoice < BETWEEN_VOICES_SECONDS) {
      return null;
    }

    const event = beat.refs[0];

    if (event === undefined) {
      return null;
    }

    let nearest: StagedBody | null = null;
    let nearestDistance = EARSHOT_TILES;

    for (const body of bodies) {
      if (this.spoken.has(body.figure)) {
        continue;
      }

      const distance = Math.hypot(body.x - from.x, body.y - from.y);

      if (distance < nearestDistance) {
        nearest = body;
        nearestDistance = distance;
      }
    }

    if (nearest === null) {
      // Nobody in earshot who has not already said it. The clock is *not* reset,
      // so the moment the player walks up to somebody who has not, they speak —
      // which is the behaviour that makes walking through a reacting town feel
      // like walking through a reacting town.
      return null;
    }

    this.sinceVoice = 0;
    this.spoken.add(nearest.figure);

    return { figure: nearest.figure, event };
  }

  /** Ends the reaction now. The caller sends the crowd home. */
  end(): void {
    this.staged = null;
    this.smoke = null;
    this.elapsed = 0;
    this.spoken.clear();
  }
}

/**
 * Where the smoke over a burning settlement is drawn from a town that can see
 * it.
 *
 * On the true bearing from the watching town, at the burning town's own distance
 * or {@link SMOKE_DISTANCE_TILES}, whichever is nearer. The facing turns the
 * plume's drift across the line of sight rather than towards or away from it: a
 * column that leans directly at the viewer is a column that looks vertical, the
 * same lesson the battlefield cairn's spear shaft learned on a screenshot
 * (`render/props.ts`).
 */
function plumeFor(struck: SettlementView | null, here: SettlementView | null): Plume | null {
  if (struck === null || here === null) {
    return null;
  }

  const spanX = struck.x - here.x;
  const spanY = struck.y - here.y;
  const distance = Math.hypot(spanX, spanY);

  if (distance < 1) {
    return null;
  }

  const drawn = Math.min(distance, SMOKE_DISTANCE_TILES);

  return {
    x: here.x + (spanX / distance) * drawn,
    y: here.y + (spanY / distance) * drawn,
    // A quarter turn off the line of sight: yaw 0 faces +y, so this is the
    // bearing to the town turned ninety degrees (`player/controller.ts`).
    facing: Math.atan2(-spanX, spanY) + Math.PI / 2,
  };
}
